import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("pgiarletta@gmail.com");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");

  const sendForgot = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      const r = await api.post("/auth/forgot-password", { email });
      setForgotMsg(r.data.message);
    } catch (err) {
      setForgotMsg(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setForgotBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const next = new URLSearchParams(window.location.search).get("next");
      await login(email, password);
      navigate(next && next.startsWith("/") && !next.startsWith("//") ? next : "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-dark min-h-screen flex items-center justify-center p-4 relative overflow-hidden" data-testid="login-page">
      <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-[#10B981]/10 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-[#1E4FD8]/15 blur-3xl" />

      <div className="relative w-full max-w-md animate-fade-up">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.png" alt="Nouvelle WhatsApp" className="h-40 w-40 mb-1 rounded-3xl bg-[#0F172A] p-3 shadow-lg" data-testid="login-logo" />
          <p className="text-slate-400 text-sm tracking-wide" data-testid="login-subtitle">Gestione servizio WhatsApp API</p>
        </div>

        <form
          onSubmit={submit}
          className="bg-[#111827]/80 backdrop-blur-md border border-[#1F2937] rounded-2xl p-8 space-y-5 shadow-2xl"
        >
          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-300">Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-[#0A0E17] border-[#1F2937] text-slate-100 h-11"
              placeholder="nome@azienda.it"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-slate-300">Password</Label>
            <Input
              id="password"
              type="password"
              data-testid="login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#0A0E17] border-[#1F2937] text-slate-100 h-11"
              placeholder="••••••••"
              required
            />
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="text-sm text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-lg px-3 py-2"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full h-11 bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold shadow-lg shadow-[#10B981]/20"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Lock className="h-4 w-4 mr-2" /> Accedi</>}
          </Button>
          <button type="button" onClick={() => { setForgot(true); setForgotMsg(""); }} data-testid="forgot-password-link"
                  className="w-full text-center text-xs text-slate-400 hover:text-[#10B981] transition-colors">Password dimenticata?</button>
        </form>
        {forgot && (
          <form onSubmit={sendForgot} className="mt-4 bg-[#111827]/80 backdrop-blur-md border border-[#1F2937] rounded-2xl p-6 space-y-3 shadow-2xl animate-fade-up" data-testid="forgot-password-form">
            <p className="text-sm text-slate-300 font-medium">Reimposta la password</p>
            <p className="text-xs text-slate-400">Inserisci la tua email: riceverai un link valido 1 ora per scegliere una nuova password.</p>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-[#0A0E17] border-[#1F2937] text-slate-100 h-10" data-testid="forgot-email-input" />
            {forgotMsg && <p className="text-xs text-[#10B981]" data-testid="forgot-password-message">{forgotMsg}</p>}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setForgot(false)} className="flex-1 h-10 border-[#1F2937] bg-transparent text-slate-300 hover:bg-[#1F2937]">Annulla</Button>
              <Button type="submit" disabled={forgotBusy} className="flex-1 h-10 bg-[#1E4FD8] hover:bg-[#173FB0] text-white" data-testid="forgot-submit-button">{forgotBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Invia link"}</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
